import React, { useCallback, useEffect, useState } from 'react';
import { API_ENDPOINTS } from '../config';
import { getBearerAuthHeaders } from '../utils/authFetchHeaders';
import './EmailReportsCard.css';

const jsonHeaders = () => ({ 'Content-Type': 'application/json', ...getBearerAuthHeaders() });

/**
 * Account page → Daily account report email: on/off, which accounts, preview and
 * a test send. Basic sees it locked; when an admin has it off for the user's
 * tier it says so instead of offering settings.
 */
export default function EmailReportsCard({ onUpgrade, showAdminSummary = false }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState('');
  const [preview, setPreview] = useState(null);
  const [summary, setSummary] = useState(null);

  const load = useCallback(async () => {
    try {
      const res = await fetch(API_ENDPOINTS.emailReportSettings, { headers: getBearerAuthHeaders() });
      const d = await res.json();
      if (!res.ok || !d.success) throw new Error(d.error || 'Couldn’t load your report settings');
      setData(d);
      setError('');
    } catch (err) {
      setError(err.message);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (!showAdminSummary) return;
    fetch(API_ENDPOINTS.emailReportAdminSummary, { headers: getBearerAuthHeaders() })
      .then((r) => r.json())
      .then((d) => { if (d.success) setSummary(d); })
      .catch(() => {});
  }, [showAdminSummary]);

  // Opened from the email's "Report settings" link (…/account#email-reports).
  useEffect(() => {
    if (data && window.location.hash === '#email-reports') {
      document.getElementById('email-reports')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [data]);

  const save = async (patch) => {
    setBusy('save');
    setNote('');
    try {
      const res = await fetch(API_ENDPOINTS.emailReportSettings, { method: 'PUT', headers: jsonHeaders(), body: JSON.stringify(patch) });
      const d = await res.json();
      if (!res.ok || !d.success) throw new Error(d.error || 'Couldn’t save');
      setData((cur) => ({ ...cur, enabled: d.enabled, accountIds: d.accountIds }));
      setNote('Saved.');
    } catch (err) {
      setNote(err.message);
    } finally {
      setBusy('');
    }
  };

  const toggleAccount = (id) => {
    const all = data.accounts.map((a) => a.id);
    const current = data.accountIds && data.accountIds.length ? data.accountIds : all;
    const next = current.includes(id) ? current.filter((x) => x !== id) : [...current, id];
    if (!next.length) { setNote('Keep at least one account, or turn the report off.'); return; }
    save({ accountIds: next.length === all.length ? null : next });
  };

  const showPreview = async () => {
    setBusy('preview');
    setNote('');
    try {
      const res = await fetch(API_ENDPOINTS.emailReportPreview, { headers: getBearerAuthHeaders() });
      const d = await res.json();
      if (!res.ok || !d.success) throw new Error(d.error || 'Couldn’t build the preview');
      if (!d.available) { setNote(d.reason === 'no connected accounts' ? 'Connect an account first.' : 'No account data yet. Try again after your accounts sync.'); return; }
      setPreview(d);
    } catch (err) {
      setNote(err.message);
    } finally {
      setBusy('');
    }
  };

  const sendTest = async () => {
    setBusy('test');
    setNote('');
    try {
      const res = await fetch(API_ENDPOINTS.emailReportSendTest, { method: 'POST', headers: jsonHeaders() });
      const d = await res.json();
      if (!res.ok || !d.success) throw new Error(d.error || 'Couldn’t send the test');
      setNote(d.sent ? `Sent to ${d.to}.` : 'Nothing to send yet: connect an account or wait for it to sync.');
    } catch (err) {
      setNote(err.message);
    } finally {
      setBusy('');
    }
  };

  if (error) return <div className="account-card er-card"><p className="er-error">{error}</p></div>;
  if (!data) return null;

  const locked = !data.allowed && (!data.tier || data.tier === 'basic');
  const offForTier = !data.allowed && !locked;
  const selected = data.accountIds && data.accountIds.length ? data.accountIds : data.accounts.map((a) => a.id);

  return (
    <div className="account-card er-card" id="email-reports">
      <div className="er-head">
        <div>
          <h3>Daily account report</h3>
          <p className="er-sub">
            An email after the close each trading day ({data.sendTime}): every connected account, today and this
            month, how that compares with the market, and anything that needs attention.
          </p>
        </div>
        {data.allowed ? (
          <label className={`er-switch${data.enabled ? ' is-on' : ''}`}>
            <input type="checkbox" checked={data.enabled} disabled={busy === 'save'} onChange={(e) => save({ enabled: e.target.checked })} />
            <span className="er-switch-track" aria-hidden="true" />
            <span>{data.enabled ? 'On' : 'Off'}</span>
          </label>
        ) : null}
      </div>

      {locked ? (
        <div className="er-locked">
          <span>The daily report is a Pro feature.</span>
          {onUpgrade ? <button type="button" className="er-btn er-btn-primary" onClick={() => onUpgrade('Upgrade to Pro to get a daily email of every connected account after the close.')}>Upgrade to Pro</button> : null}
        </div>
      ) : null}
      {offForTier ? <p className="er-sub">The daily report isn’t turned on for your plan right now.</p> : null}

      {data.allowed ? (
        <>
          {data.accounts.length ? (
            <div className="er-accounts">
              <div className="er-label">Accounts in the report</div>
              {data.accounts.map((a) => (
                <label key={a.id} className="er-account">
                  <input type="checkbox" checked={selected.includes(a.id)} disabled={!data.enabled || busy === 'save'} onChange={() => toggleAccount(a.id)} />
                  <span>{a.name}</span>
                  {a.broker && a.broker !== a.name ? <span className="er-muted">{a.broker}</span> : null}
                </label>
              ))}
            </div>
          ) : (
            <p className="er-sub">Connect an account on My Portfolio and your first report goes out after the next close.</p>
          )}
          <div className="er-actions">
            <button type="button" className="er-btn" onClick={showPreview} disabled={busy === 'preview' || !data.accounts.length}>
              {busy === 'preview' ? 'Building…' : 'Preview today’s report'}
            </button>
            <button type="button" className="er-btn" onClick={sendTest} disabled={busy === 'test' || !data.accounts.length}>
              {busy === 'test' ? 'Sending…' : 'Email me a test'}
            </button>
            {note ? <span className="er-note">{note}</span> : null}
          </div>
        </>
      ) : null}

      {showAdminSummary && summary ? (
        <div className="er-admin">
          <div className="er-label">Admin · last 7 days</div>
          {summary.days.length ? (
            <ul>
              {summary.days.map((d) => (
                <li key={d.date}><strong>{d.date}</strong> · {d.sent || 0} sent · {d.skipped || 0} skipped · {d.failed || 0} failed</li>
              ))}
            </ul>
          ) : <p className="er-sub">No reports sent yet.</p>}
          {summary.failures.length ? (
            <details>
              <summary>Recent failures ({summary.failures.length})</summary>
              <ul>{summary.failures.map((f) => <li key={`${f.date}-${f.email}`}>{f.date} · {f.email} · {f.reason}</li>)}</ul>
            </details>
          ) : null}
          <p className="er-sub">Turn the report off for a tier, or everyone, on Admin → Users → Feature access (“Daily account report email”).</p>
        </div>
      ) : null}

      {preview ? (
        <div className="er-modal" role="dialog" aria-modal="true" aria-label="Report preview">
          <button type="button" className="er-modal-backdrop" aria-label="Close" onClick={() => setPreview(null)} />
          <div className="er-modal-box">
            <div className="er-modal-head">
              <strong>{preview.subject}</strong>
              <button type="button" className="er-btn" onClick={() => setPreview(null)}>Close</button>
            </div>
            <iframe title="Report preview" className="er-frame" srcDoc={preview.html} sandbox="" />
          </div>
        </div>
      ) : null}
    </div>
  );
}
